import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Container, Stack, Typography, Box, Button, List, ListItem, ListItemText, Divider, CircularProgress, IconButton, Collapse, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { TextField, Chip } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Select from 'react-select';
import PlateformService from '../../components/PlateformService';
import axios from 'axios';
import { header } from '../../components/header.encry';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

export default function KooulSyncConsole() {
  const [platforms, setPlatforms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [openNodes, setOpenNodes] = useState({});
  const [search, setSearch] = useState('');
  // queue state for Sync All
  const [queue, setQueue] = useState([]); // { remoteId, category, status, errorMsg }
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [queuePaused, setQueuePaused] = useState(false);
  const processingRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await PlateformService.fetchPlateforms();
        const list = (res?.response || res || []).filter(p => ((p.type||'').toLowerCase() === 'kooul'));
        setPlatforms(list.map(p => ({ value: (p._id && (p._id.$oid || p._id)) ? (p._id.$oid || p._id) : (p.id || p._id), label: p.name || p.title, raw: p })));
      } catch (e) {
        console.error(e);
        setPlatforms([]);
      }
    };
    load();
  }, []);

  const handlePlatformChange = async (opt) => {
    setSelected(opt);
    setCategories([]);
    if (!opt) return;
    setLoadingCats(true);
    try {
      // try to fetch categories from backend by plateform_id
      const resp = await axios.get(`${API_URL}/productsCategories`, { ...header(), params: { plateform_id: opt.value } });
      let data = resp?.data?.response || resp?.data || [];
      // filter by plateform_id in returned items (normalize shapes)
      const normalizedPlateformId = String(opt.value || '').replace(/\W/g, '');
      data = (Array.isArray(data) ? data : []).filter((it) => {
        const pid = it?.plateform_id && (it.plateform_id.$oid || it.plateform_id) ? (it.plateform_id.$oid || it.plateform_id) : null;
        return !pid || String(pid).includes(normalizedPlateformId) || String(opt.value).includes(String(pid));
      });
      setCategories(data);
    } catch (err) {
      console.error(err);
      toast.error('Impossible de charger les catégories locales pour cette plateforme.');
      setCategories([]);
    } finally {
      setLoadingCats(false);
    }
  };

  const syncCategory = async (cat) => {
    if (!selected) {
      toast.error('Sélectionnez d\u00e9j\u00e0 une plateforme Kooul');
      return;
    }
    const remoteId = cat.remote_plateform_id || cat.remote_id || cat.remoteId || cat.value || cat.id || (cat._id && (cat._id.$oid || cat._id));
    if (!remoteId) {
      toast.error('Catégorie distante introuvable pour cette entrée');
      return;
    }
    setSyncing(prev => ({ ...prev, [remoteId]: true }));
    try {
      const platformId = selected?.value && (selected.value.$oid || selected.value) ? (selected.value.$oid || selected.value) : selected?.value;
      const payload = {
        plateform_id: String(platformId),
        category_remote_id: String(remoteId),
        store_id: cat.store_id || cat.storeId || (selected && selected.raw && selected.raw.store_id) || undefined,
        force_sync: false,
        page_size: 100,
      };
      console.debug('KooulSync: queuing category sync payload', payload);
      const res = await PlateformService.syncKooulCategoryProducts(payload);
      console.debug('KooulSync: queue response', res);
      toast.success(res?.message || 'Synchronisation de la catégorie mise en file (queued)');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du déclenchement de la synchronisation');
    } finally {
      setSyncing(prev => ({ ...prev, [remoteId]: false }));
    }
  };

  const getLeafNodes = (nodes) => {
    const leaves = [];
    const walk = (n) => {
      if (!n) return;
      const isLeaf = !n.children || n.children.length === 0;
      const isRoot = !!n.isRoot;
      if (isLeaf && !isRoot) {
        leaves.push(n.item);
        return;
      }
      if (n.children && n.children.length) {
        n.children.forEach(c => walk(c));
      }
    };
    (nodes || []).forEach(n => walk(n));
    return leaves;
  };

  const startQueueProcessing = async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    const processNext = async () => {
      if (queuePaused) {
        processingRef.current = false;
        return;
      }

      const nextIndex = queue.findIndex(q => q.status === 'pending');
      if (nextIndex === -1) {
        processingRef.current = false;
        return;
      }

      const item = queue[nextIndex];
      const remoteId = item.remoteId;
      setQueue(q => q.map((it, idx) => idx === nextIndex ? { ...it, status: 'in-progress' } : it));
      setSyncing(s => ({ ...s, [remoteId]: true }));
      try {
        const platformId = selected?.value && (selected.value.$oid || selected.value) ? (selected.value.$oid || selected.value) : selected?.value;
        const payload = {
          plateform_id: String(platformId),
          category_remote_id: String(remoteId),
          store_id: item.category.store_id || item.category.storeId || (selected && selected.raw && selected.raw.store_id) || undefined,
          force_sync: false,
          page_size: 100,
        };
        await PlateformService.syncKooulCategoryProducts(payload);
        setQueue(q => q.map((it, idx) => idx === nextIndex ? { ...it, status: 'done' } : it));
      } catch (e) {
        console.error('Queue item error', remoteId, e);
        setQueue(q => q.map((it, idx) => idx === nextIndex ? { ...it, status: 'error', errorMsg: e?.message || 'Erreur' } : it));
      } finally {
        setSyncing(s => ({ ...s, [remoteId]: false }));
        // small delay before next
        await new Promise(r => setTimeout(r, 200));
        if (!queuePaused) {
          // re-read queue from state (closure capture) via updater
          const current = (Array.isArray(queue) ? queue : []);
          const stillPending = current.some(q => q.status === 'pending');
          if (stillPending) {
            await processNext();
          } else {
            processingRef.current = false;
          }
        } else {
          processingRef.current = false;
        }
      }
    };

    // start chain
    await processNext();
  };

  const syncAll = async () => {
    if (!selected) { toast.error('Sélectionnez une plateforme Kooul'); return; }
    const leaves = getLeafNodes(tree || []);
    if (!leaves.length) { toast.info('Aucune catégorie feuille à synchroniser'); return; }

    const normalizeRemoteIds = (cat) => {
      const candidates = [];
      if (!cat) return [];
      if (cat.remote_plateform_id) candidates.push(cat.remote_plateform_id);
      if (cat.remote_id) {
        if (Array.isArray(cat.remote_id)) candidates.push(...cat.remote_id);
        else candidates.push(cat.remote_id);
      }
      if (cat.remoteId) candidates.push(cat.remoteId);
      if (cat.value) candidates.push(cat.value);
      if (cat.id) candidates.push(cat.id);
      if (cat._id && (cat._id.$oid || cat._id)) candidates.push(cat._id.$oid || cat._id);
      // flatten, stringified, unique and non-empty
      return Array.from(new Set(candidates.map(c => {
        if (c === null || c === undefined) return '';
        if (typeof c === 'object') return (c.$oid || c._id || c.id || c.value || '').toString();
        return String(c);
      }).filter(Boolean)));
    };

    // Build per-remoteId queue entries (one entry per remote id)
    const allEntries = [];
    leaves.forEach(cat => {
      const remoteIds = normalizeRemoteIds(cat);
      remoteIds.forEach(rid => allEntries.push({ remoteId: rid, category: cat }));
    });

    // dedupe by remoteId preserving first occurrence
    const seen = new Set();
    const initialQueue = allEntries.reduce((acc, it) => {
      if (!it || !it.remoteId) return acc;
      if (seen.has(it.remoteId)) return acc;
      seen.add(it.remoteId);
      acc.push({ remoteId: it.remoteId, category: it.category, status: 'pending', errorMsg: '' });
      return acc;
    }, []);

    if (!initialQueue.length) { toast.info('Aucune catégorie distante trouvée'); return; }

    setQueue(initialQueue);
    setQueueModalOpen(true);
    setQueuePaused(false);
    // kick off processing
    setTimeout(() => startQueueProcessing(), 100);
  };

  const toggleQueuePause = () => {
    setQueuePaused(prev => {
      const next = !prev;
      if (!next) {
        // resumed -> restart processing
        setTimeout(() => startQueueProcessing(), 100);
      }
      return next;
    });
  };
  

  const buildTree = (items) => {
    const map = {};
    const roots = [];

    const getId = (it) => it && (it._id?.$oid || it._id || it.id || it.value || it.key || null);
    const getParent = (it) => {
      if (!it) return null;
      if (it.parent) return it.parent;
      if (it.parent_id) return it.parent_id.$oid || it.parent_id;
      if (it.parentId) return it.parentId;
      return null;
    };

    items.forEach((it) => {
      const id = getId(it) || String(Math.random());
      map[id] = { __id: id, item: it, children: [], isRoot: false };
    });

    items.forEach((it) => {
      const id = getId(it) || null;
      const parentRaw = getParent(it);
      const parentId = parentRaw && (parentRaw.$oid || parentRaw) || parentRaw;
      const node = map[id];
      if (parentId && map[parentId]) {
        map[parentId].children.push(node);
      } else {
        node.isRoot = true;
        roots.push(node);
      }
    });

    // Remove standalone roots that have no parent and no children
    const filteredRoots = roots.filter(n => {
      const hasChildren = Array.isArray(n.children) && n.children.length > 0;
      const parentExists = getParent(n.item) !== null && getParent(n.item) !== undefined;
      // keep node if it has children or it has a parent (shouldn't happen for roots), otherwise drop
      return hasChildren || parentExists;
    });
    return filteredRoots;
  };

  const tree = useMemo(() => buildTree(categories || []), [categories]);

  // initialize open/collapse state for root nodes (collapsed by default)
  useEffect(() => {
    const init = {};
    (tree || []).forEach(n => {
      if (n && n.__id) init[n.__id] = false;
    });
    setOpenNodes(init);
  }, [tree]);

  // when search changes, expand parents of matching nodes so children are visible
  useEffect(() => {
    if (!search || !search.trim()) {
      // collapse roots when clearing search
      const init = {};
      (tree || []).forEach(n => { if (n && n.__id) init[n.__id] = false; });
      setOpenNodes(init);
      return;
    }

    const q = String(search).toLowerCase().trim();
    // build quick lookup by id
    const idFor = (it) => it && (it._id?.$oid || it._id || it.id || it.value || it.key || null);
    const parentFor = (it) => {
      if (!it) return null;
      if (it.parent) return it.parent;
      if (it.parent_id) return it.parent_id.$oid || it.parent_id;
      if (it.parentId) return it.parentId;
      return null;
    };

    const flat = Array.isArray(categories) ? categories : [];
    const mapById = {};
    flat.forEach(it => { const id = idFor(it) || String(Math.random()); mapById[id] = it; });

    const toOpen = {};

    flat.forEach(it => {
      const label = (it.name || it.label || it.title || '').toString().toLowerCase();
      const remote = (it.remote_plateform_id || it.remote_id || it.remoteId || it.value || it.id || it._id || '').toString().toLowerCase();
      if (label.includes(q) || remote.includes(q)) {
        // walk ancestors and mark their root ids to open
        let parent = parentFor(it);
        while (parent) {
          const pid = parent && (parent.$oid || parent) ? (parent.$oid || parent) : parent;
          // find ancestor node in tree roots map
          // mark the root ancestor open
          // climb up
          const parentItem = mapById[pid];
          if (!parentItem) break;
          // find the root node id for this parent item
          // parentItem may itself have parent; continue walking
          parent = parentFor(parentItem);
        }
      }
    });

    // simple behavior: open all roots if search is present so matches are visible
    const init = {};
    (tree || []).forEach(n => { if (n && n.__id) init[n.__id] = true; });
    setOpenNodes(init);
  }, [search, categories, tree]);

  const toggleNode = (id) => setOpenNodes(prev => ({ ...prev, [id]: !prev[id] }));

  const renderNode = (node, level = 0) => {
    const it = node.item || {};
    const label = it.name || it.label || it.title || String(node.__id);
    const remoteId = it.remote_plateform_id || it.remote_id || it.remoteId || it.value || it.id || it._id;
    const isLeaf = !node.children || node.children.length === 0;
    const isRoot = !!node.isRoot;
    return (
      <React.Fragment key={node.__id}>
        <ListItem sx={{ pl: 2 + level * 2 }}>
          {isRoot && (
            <IconButton size="small" onClick={() => toggleNode(node.__id)}>
              {openNodes[node.__id] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
          <ListItemText primary={label} secondary={remoteId ? `remote_id: ${remoteId}` : null} />
          {isLeaf && !isRoot && (
            <Button
              variant="contained"
              disabled={!!syncing[remoteId]}
              onClick={() => syncCategory(it)}
            >
              {syncing[remoteId] ? 'En cours...' : 'Sync'}
            </Button>
          )}
        </ListItem>
        {isRoot ? (
          <Collapse in={!!openNodes[node.__id]} timeout="auto" unmountOnExit>
            {node.children && node.children.map((c) => renderNode(c, level + 1))}
          </Collapse>
        ) : (
          node.children && node.children.map((c) => renderNode(c, level + 1))
        )}
        <Divider />
      </React.Fragment>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4">Kooul - Sync</Typography>
          <Typography variant="body2" color="text.secondary">Sélectionnez une plateforme Kooul pour afficher les catégories locales et synchroniser par catégorie.</Typography>
        </Box>

        <Box>
          <Select options={platforms} value={selected} onChange={handlePlatformChange} placeholder="Sélectionner une plateforme Kooul" />
        </Box>

        <Box>
          <TextField
            label="Rechercher une catégorie"
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom ou remote_id"
          />
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>Sync Products (par catégorie)</Typography>
            <Button variant="contained" size="small" disabled={!selected || loadingCats} onClick={syncAll}>
              {loadingCats ? 'Loading...' : 'Sync All'}
            </Button>
          </Box>
          {loadingCats ? (
            <CircularProgress size={24} />
          ) : (
            <List>
              {(!categories || categories.length === 0) && (
                <ListItem>
                  <ListItemText primary="Aucune catégorie locale trouvée pour cette plateforme." />
                </ListItem>
              )}
              {tree.map((n) => renderNode(n, 0))}
            </List>
          )}
        </Box>

        <Box>
          <Typography variant="h6">Sync Categories (service)</Typography>
          <Typography variant="body2" color="text.secondary">Vous pouvez utiliser l'outil de synchronisation des catégories Kooul (service /kooul/sync-categories).</Typography>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={async () => {
            if (!selected) { toast.error('Sélectionnez une plateforme'); return; }
            try {
              await PlateformService.syncKooulCategories({ plateform_id: selected.value });
              toast.success('Synchronisation catégories déclenchée');
            } catch (e) { console.error(e); toast.error('Erreur lors de la synchronisation des catégories'); }
          }}>Sync Categories</Button>
        </Box>
      </Stack>
      
      <Dialog open={queueModalOpen} onClose={() => setQueueModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Queue</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button variant="contained" size="small" onClick={toggleQueuePause}>{queuePaused ? 'Resume' : 'Pause'}</Button>
            <Typography variant="body2">{queue.length} items in queue</Typography>
          </Box>
          <List>
            {queue.length === 0 && (
              <ListItem><ListItemText primary="Queue vide" /></ListItem>
            )}
            {queue.map((it, idx) => (
              <ListItem key={idx} secondaryAction={it.status === 'in-progress' ? <CircularProgress size={18} /> : null}>
                <ListItemText primary={it.category?.name || it.category?.label || String(it.remoteId)} secondary={`remote_id: ${it.remoteId} — status: ${it.status}${it.errorMsg ? ' — ' + it.errorMsg : ''}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQueueModalOpen(false); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
